import React, { useCallback, useEffect, useState } from 'react';
import { ArrowDownRight, ArrowUpRight, Wallet, Clock } from 'lucide-react';
import { AppLayout } from '../../components/layout/AppLayout';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import {
  earningsApi,
  EarningsSummary,
  PayoutStatus,
  TimelineEntry,
  VendorPayout,
} from '../../api/earnings';
import { formatNgn, formatDateTime } from '../../utils/format';
import { RequestPayoutModal } from './RequestPayoutModal';
import { PayoutBankModal } from './PayoutBankModal';

const PAYOUT_VARIANT: Record<
  PayoutStatus,
  'success' | 'warning' | 'danger' | 'info'
> = {
  pending: 'warning',
  processing: 'info',
  completed: 'success',
  failed: 'danger',
};

type Tab = 'timeline' | 'payouts';

export const Earnings: React.FC = () => {
  const toast = useToast();
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [payouts, setPayouts] = useState<VendorPayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('timeline');
  const [showRequest, setShowRequest] = useState(false);
  const [showBank, setShowBank] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [s, t, p] = await Promise.all([
        earningsApi.getSummary(),
        earningsApi.getTimeline(),
        earningsApi.listPayouts(),
      ]);
      setSummary(s);
      setTimeline(t.items);
      setPayouts(p.items);
    } catch (err) {
      toast.error(err, 'Failed to load earnings.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  return (
    <AppLayout>
      <PageHeader
        title="Earnings"
        description="Track escrow releases, request payouts, and review past withdrawals."
        actions={
          <>
            <Button variant="secondary" onClick={() => setShowBank(true)}>
              Payout bank
            </Button>
            <Button
              onClick={() => {
                // Enforce a payout bank before any withdrawal — if none is set,
                // open the bank modal instead so the vendor can't get stuck.
                if (summary && !summary.hasPayoutBank) {
                  toast.error('Add a payout bank account before withdrawing.');
                  setShowBank(true);
                  return;
                }
                setShowRequest(true);
              }}
              disabled={!summary || summary.payoutBalance <= 0}
            >
              Withdraw
            </Button>
          </>
        }
      />

      {loading || !summary ? (
        <Card>
          <div
            style={{
              padding: 32,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Spinner />
          </div>
        </Card>
      ) : (
        <>
          {/* Stat cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 16,
              marginBottom: 16,
            }}
          >
            <StatCard
              icon={<Wallet size={18} />}
              label="Available to withdraw"
              value={formatNgn(summary.payoutBalance)}
              highlight
            />
            <StatCard
              icon={<Clock size={18} />}
              label="Locked in escrow"
              value={formatNgn(summary.lockedInEscrow)}
              hint="Released once buyers confirm receipt."
            />
            <StatCard
              icon={<ArrowUpRight size={18} />}
              label="Released last 7 days"
              value={formatNgn(summary.releasedLast7Days)}
            />
            <StatCard
              icon={<ArrowUpRight size={18} />}
              label="Released last 30 days"
              value={formatNgn(summary.releasedLast30Days)}
            />
          </div>

          {!summary.hasPayoutBank ? (
            <div
              style={{
                marginBottom: 16,
                padding: 14,
                background: 'var(--yellow-100)',
                color: 'var(--yellow-900)',
                borderRadius: 'var(--radius-md)',
                fontSize: 13,
              }}
            >
              <strong>Add your payout bank</strong> before requesting a
              withdrawal — we send all payouts to the account on file.
              &nbsp;
              <button
                onClick={() => setShowBank(true)}
                style={{
                  color: 'var(--yellow-900)',
                  textDecoration: 'underline',
                  fontWeight: 600,
                }}
              >
                Add bank details →
              </button>
            </div>
          ) : null}

          {/* Tabs */}
          <Card padding={0}>
            <div
              style={{
                display: 'flex',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <TabBtn
                active={tab === 'timeline'}
                onClick={() => setTab('timeline')}
              >
                Activity
              </TabBtn>
              <TabBtn
                active={tab === 'payouts'}
                onClick={() => setTab('payouts')}
              >
                Payouts ({payouts.length})
              </TabBtn>
            </div>

            {tab === 'timeline' ? (
              <TimelineList entries={timeline} />
            ) : (
              <PayoutsList items={payouts} />
            )}
          </Card>
        </>
      )}

      {showRequest && summary ? (
        <RequestPayoutModal
          available={summary.payoutBalance}
          minPayout={summary.minPayout}
          feePercent={summary.payoutFeePercent}
          feeCap={summary.payoutFeeCap}
          onClose={() => setShowRequest(false)}
          onSubmitted={() => {
            setShowRequest(false);
            void fetchAll();
          }}
        />
      ) : null}

      {showBank ? (
        <PayoutBankModal
          onClose={() => setShowBank(false)}
          onSaved={() => {
            setShowBank(false);
            void fetchAll();
          }}
        />
      ) : null}
    </AppLayout>
  );
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  highlight?: boolean;
}> = ({ icon, label, value, hint, highlight }) => (
  <div
    style={{
      background: highlight ? 'var(--primary)' : 'var(--bg-card)',
      color: highlight ? 'var(--text-inverse)' : 'var(--text-primary)',
      border: '1px solid',
      borderColor: highlight ? 'var(--primary)' : 'var(--border)',
      borderRadius: 'var(--radius-lg)',
      padding: 20,
      boxShadow: 'var(--shadow-sm)',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
        opacity: 0.85,
        fontSize: 13,
        fontWeight: 500,
      }}
    >
      {icon}
      {label}
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
      {value}
    </div>
    {hint ? (
      <div style={{ fontSize: 12, opacity: 0.8 }}>{hint}</div>
    ) : null}
  </div>
);

const TabBtn: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      padding: '14px 20px',
      fontSize: 14,
      fontWeight: 600,
      color: active ? 'var(--primary)' : 'var(--text-secondary)',
      borderBottom: `2px solid ${active ? 'var(--primary)' : 'transparent'}`,
      marginBottom: -1,
    }}
  >
    {children}
  </button>
);

const TimelineList: React.FC<{ entries: TimelineEntry[] }> = ({ entries }) => {
  if (entries.length === 0) {
    return (
      <EmptyState
        title="No activity yet"
        description="Escrow releases and payouts will show up here."
      />
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {entries.map((e) => {
        const isRelease = e.kind === 'release';
        const isShippingFee = e.kind === 'shipping_fee';
        const orderId = String((e.meta as { orderId?: string }).orderId ?? '');
        const reference = String((e.meta as { reference?: string }).reference ?? '');
        const title = isRelease
          ? 'Escrow released'
          : isShippingFee
            ? 'Shipping fee'
            : 'Payout';
        const subtitle = isShippingFee
          ? ` · order ${orderId} · deducted from payout`
          : isRelease
            ? ` · order ${orderId}`
            : ` · ${reference}`;
        return (
          <div
            key={`${e.kind}-${e.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '14px 20px',
              borderTop: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-full)',
                background: isRelease ? 'var(--green-100)' : 'var(--neutral-200)',
                color: isRelease ? 'var(--green-800)' : 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isRelease ? (
                <ArrowDownRight size={18} />
              ) : (
                <ArrowUpRight size={18} />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500 }}>{title}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {formatDateTime(e.at)}
                {subtitle.slice(0, 90)}
              </div>
            </div>
            <div
              style={{
                fontWeight: 600,
                color: isRelease ? 'var(--green-800)' : 'var(--text-primary)',
              }}
            >
              {isRelease ? '+' : ''}
              {formatNgn(e.amount)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PayoutsList: React.FC<{ items: VendorPayout[] }> = ({ items }) => {
  if (items.length === 0) {
    return (
      <EmptyState
        title="No payouts yet"
        description="Request a withdrawal once you have a balance to move."
      />
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}
      >
        <thead>
          <tr
            style={{
              background: 'var(--bg-muted)',
              color: 'var(--text-secondary)',
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            <th style={th}>Reference</th>
            <th style={th}>Amount</th>
            <th style={th}>Bank</th>
            <th style={th}>Status</th>
            <th style={th}>Requested</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={td}>
                <code style={{ fontSize: 13 }}>{p.reference}</code>
              </td>
              <td style={td}>{formatNgn(p.amount)}</td>
              <td style={td}>
                <div style={{ fontSize: 13 }}>{p.accountName}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  •••• {p.accountNumber.slice(-4)}
                </div>
              </td>
              <td style={td}>
                <Badge variant={PAYOUT_VARIANT[p.status]}>{p.status}</Badge>
                {p.status === 'failed' && p.failureReason ? (
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--red-900)',
                      marginTop: 4,
                    }}
                  >
                    {p.failureReason}
                  </div>
                ) : null}
              </td>
              <td style={td}>{formatDateTime(p.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const th: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontWeight: 600,
};
const td: React.CSSProperties = {
  padding: '14px 16px',
  verticalAlign: 'middle',
};
